"use client";
import { useState, useEffect } from "react";
import Image from "next/image";
import Nav from "./Nav";
import Footer from "./Footer";
import FlagshipCarousel from "./FlagshipCarousel";
import { useMedia, FadeIn } from "../lib/hooks";
import { getPostsBySlugs } from "../blog/posts";
import RelatedPostCard from "../blog/components/RelatedPostCard";
import { softwareAppSchema } from "../lib/software-application";

const BG = "#09090b";
const BG_ALT = "#0c0c0e";
const TEXT = "#EDEDED";
const TEXT_MUTED = "#a0a0a0";
const TEAL = "#4FCDC4";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const MAX_W = 1054;

// Normalize a date string to ISO 8601 with Pacific timezone.
// Accepts "YYYY-MM-DD" or already-formatted ISO datetime, returns ISO datetime.
function toIsoDateTime(date) {
  if (!date) return null;
  if (typeof date === "string" && date.includes("T")) return date;
  return `${date}T00:00:00-07:00`;
}

function PrimaryBtn({ href, children, white }) {
  const [h, setH] = useState(false);
  return (
    <a href={href} onMouseOver={() => setH(true)} onMouseOut={() => setH(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        padding: "12px 28px", borderRadius: 10,
        background: white ? "#FFFFFF" : TEAL, color: "#000", fontFamily: "var(--body)",
        fontWeight: 600, fontSize: 15, textDecoration: "none",
        transition: "opacity 0.2s", opacity: h ? 0.9 : 1,
      }}
    >{children}</a>
  );
}

function SecondaryBtn({ href, children }) {
  const [h, setH] = useState(false);
  return (
    <a href={href} onMouseOver={() => setH(true)} onMouseOut={() => setH(false)}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        padding: "12px 28px", borderRadius: 10,
        background: h ? "rgba(255,255,255,0.04)" : "transparent",
        color: TEXT, fontFamily: "var(--body)", fontWeight: 600, fontSize: 15,
        border: `1px solid ${h ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.12)"}`,
        textDecoration: "none", transition: "background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s, transform 0.15s",
      }}
    >{children}</a>
  );
}

const h2Style = {
  fontFamily: "var(--display)", fontSize: "clamp(32px, 4vw, 48px)",
  fontWeight: 700, letterSpacing: "-0.031em", color: TEXT, lineHeight: 1.1,
};

export default function PlatformPageTemplate({
  heroTitle, heroSubtitle, heroLabel, lastUpdated, definitionParagraph,
  overviewCards = [], features = [], connections = [], faqs = [],
  cta = {},
  // Canonical route of the page ("/platform/aeo"). When set, the template emits
  // a BreadcrumbList (Home -> heroTitle) and the shared SoftwareApplication
  // node (app/lib/software-application.js) for the page. All 5 platform pages
  // pass it; optional so any non-platform caller stays unchanged.
  route = null,
  // Optional Profound-style sections (added 2026-04-24 for AEI pilot).
  // All default to null/empty so other platform pages stay unchanged.
  preFeatures = null,            // JSX rendered between overview and features
  methodology = null,            // { heading, subhead, claims: [{ title, description }] }
  platformsCoverage = null,      // { heading, subhead, platforms: [{ name, slug, hex }] }
  trustSignals = null,           // { heading, items: [{ title, subtext? }] }
  relatedPosts = [],             // array of post slug strings; resolved via getPostsBySlugs
  relatedHeading = "Read more",  // optional override for the related-posts section heading
  flagshipScreenshot = null,     // { src, alt } | [{ src, alt }, ...] — single shot OR auto-rotating carousel rendered after hero (Profound-style mac-window frame)
  flagshipIntervalMs = 4500,     // ms between carousel transitions (ignored if only 1 screenshot)
  fiveAStage = null,             // { num, action, anchor } — renders a "Stage N of the 5 A's" eyebrow above the H1 linking back to /ai-marketing-framework (added 2026-08-01; closes the one-way framework link graph)
}) {
  // Normalize flagship into an array (accept both single object + array)
  const flagshipImages = !flagshipScreenshot
    ? []
    : Array.isArray(flagshipScreenshot)
    ? flagshipScreenshot
    : [flagshipScreenshot];
  const resolvedRelatedPosts = relatedPosts.length > 0 ? getPostsBySlugs(relatedPosts) : [];
  const isMobile = useMedia("(max-width: 768px)");

  return (
    <div style={{ background: BG, minHeight: "100vh" }}>
      {route && (
        <>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.ai-advisors.ai" },
              { "@type": "ListItem", "position": 2, "name": heroTitle, "item": `https://www.ai-advisors.ai${route}` },
            ],
          }) }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareAppSchema) }} />
        </>
      )}
      <Nav />
      <main>
        {/* Hero */}
        <section style={{ background: BG, paddingTop: isMobile ? 120 : 160, paddingBottom: isMobile ? 60 : 100, textAlign: "center" }}>
          <div style={{ maxWidth: MAX_W, margin: "0 auto", padding: "0 24px" }}>
            <FadeIn>
              {fiveAStage && (
                <a
                  href={`/ai-marketing-framework#${fiveAStage.anchor}`}
                  style={{
                    display: "inline-block", marginBottom: 16,
                    fontFamily: "var(--mono)", fontSize: 12, fontWeight: 600,
                    color: TEAL, textDecoration: "none", textTransform: "uppercase",
                    letterSpacing: "0.08em", padding: "4px 14px", borderRadius: 6,
                    background: "rgba(79,205,196,0.1)", border: "1px solid rgba(79,205,196,0.2)",
                  }}
                >
                  Stage {fiveAStage.num} of the 5 A&apos;s · {fiveAStage.action}
                </a>
              )}
              <h1 style={{ ...h2Style, margin: "0 auto", maxWidth: 800 }}>{heroTitle}</h1>
              {heroLabel && (
                <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_MUTED, marginTop: 12 }}>{heroLabel}</p>
              )}
            </FadeIn>
            <FadeIn delay={0.1}>
              <p style={{
                fontFamily: "var(--body)", fontSize: isMobile ? 17 : 19, color: TEXT_MUTED,
                maxWidth: 600, margin: "20px auto 0", lineHeight: 1.7,
              }}>{heroSubtitle}</p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginTop: 36 }}>
                <PrimaryBtn href="https://app.ai-advisors.ai/signup?utm_source=website&utm_medium=platform&utm_campaign=signup&utm_content=platform_cta">Start Free Trial</PrimaryBtn>
                <SecondaryBtn href="#features">See all features</SecondaryBtn>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Flagship carousel — Profound-style, mac-window framed, immediately after hero */}
        {flagshipImages.length > 0 && (
          <section style={{ background: BG, padding: isMobile ? "0 16px 60px" : "0 24px 100px" }}>
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
              <FadeIn>
                <FlagshipCarousel images={flagshipImages} intervalMs={flagshipIntervalMs} />
              </FadeIn>
            </div>
          </section>
        )}

        {/* Overview cards */}
        {overviewCards.length > 0 && (
          <section style={{ background: BG_ALT, padding: isMobile ? "60px 20px" : "100px 24px" }}>
            <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16 }}>
                {overviewCards.map((card, i) => (
                  <FadeIn key={card.title} delay={i * 0.08}>
                    <div style={{ border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: "28px 24px" }}>
                      <div style={{ fontFamily: "var(--display)", fontSize: 18, fontWeight: 600, color: TEXT, marginBottom: 8 }}>{card.title}</div>
                      <div style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT_MUTED, lineHeight: 1.6 }}>{card.description}</div>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* PreFeatures (e.g., 4-mockup grid teaser above features) */}
        {preFeatures && (
          <section style={{ background: BG, padding: isMobile ? "60px 20px 0" : "100px 24px 0" }}>
            <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
              {preFeatures}
            </div>
          </section>
        )}

        {/* Features */}
        {features.length > 0 && (
          <section id="features" style={{ background: BG, padding: isMobile ? "80px 20px" : "120px 24px" }}>
            <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
              {features.map((feat, i) => (
                <FadeIn key={feat.title}>
                  <div id={feat.id} style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : (feat.reversed ? "row-reverse" : "row"),
                    alignItems: "center",
                    gap: isMobile ? 40 : 64,
                    marginBottom: i < features.length - 1 ? (isMobile ? 80 : 120) : 0,
                    scrollMarginTop: 80,
                  }}>
                    <div style={{ flex: "0 0 38%", maxWidth: isMobile ? "100%" : "38%" }}>
                      <h2 style={{
                        fontFamily: "var(--display)", fontSize: "clamp(24px, 3vw, 32px)",
                        fontWeight: 600, letterSpacing: "-0.031em", color: TEXT, lineHeight: 1.2, marginBottom: 16,
                      }}>{feat.title}</h2>
                      <p style={{ fontFamily: "var(--body)", fontSize: 17, color: TEXT_MUTED, lineHeight: 1.7, margin: 0 }}>{feat.description}</p>
                    </div>
                    <div style={{ flex: 1, width: "100%", minWidth: 0, overflow: "hidden" }}>{feat.mockup}</div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </section>
        )}

        {/* Methodology (3-column credibility section) */}
        {methodology && (
          <section style={{ background: BG_ALT, padding: isMobile ? "80px 20px" : "120px 24px" }}>
            <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
              <FadeIn>
                <h2 style={{
                  fontFamily: "var(--display)", fontSize: "clamp(24px, 3vw, 32px)",
                  fontWeight: 600, letterSpacing: "-0.031em", color: TEXT, lineHeight: 1.2,
                  textAlign: "center", marginBottom: 12, maxWidth: 760, marginLeft: "auto", marginRight: "auto",
                }}>{methodology.heading}</h2>
                {methodology.subhead && (
                  <p style={{
                    fontFamily: "var(--body)", fontSize: 17, color: TEXT_MUTED,
                    textAlign: "center", maxWidth: 640, margin: "0 auto 56px", lineHeight: 1.6,
                  }}>{methodology.subhead}</p>
                )}
              </FadeIn>
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : `repeat(${Math.min(methodology.claims.length, 3)}, 1fr)`,
                gap: 32,
              }}>
                {methodology.claims.map((claim, i) => (
                  <FadeIn key={claim.title} delay={i * 0.08}>
                    <div>
                      <div style={{
                        fontFamily: "var(--display)", fontSize: 18, fontWeight: 600,
                        color: TEXT, marginBottom: 10, letterSpacing: "-0.017em",
                      }}>{claim.title}</div>
                      <div style={{
                        fontFamily: "var(--body)", fontSize: 15, color: TEXT_MUTED,
                        lineHeight: 1.7,
                      }}>{claim.description}</div>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Platforms Coverage (logo strip) */}
        {platformsCoverage && (
          <section style={{ background: BG, padding: isMobile ? "60px 20px" : "100px 24px" }}>
            <div style={{ maxWidth: 900, margin: "0 auto" }}>
              <FadeIn>
                <h2 style={{
                  fontFamily: "var(--display)", fontSize: "clamp(22px, 2.8vw, 28px)",
                  fontWeight: 600, letterSpacing: "-0.018em", color: TEXT, lineHeight: 1.25,
                  textAlign: "center", marginBottom: 10,
                }}>{platformsCoverage.heading}</h2>
                {platformsCoverage.subhead && (
                  <p style={{
                    fontFamily: "var(--body)", fontSize: 15, color: TEXT_MUTED,
                    textAlign: "center", margin: "0 auto 40px", lineHeight: 1.6,
                    maxWidth: 580,
                  }}>{platformsCoverage.subhead}</p>
                )}
              </FadeIn>
              <FadeIn delay={0.1}>
                <div style={{
                  border: `1px solid ${CARD_BORDER}`, borderRadius: 12,
                  padding: isMobile ? "28px 16px" : "40px 32px",
                  display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center",
                  gap: isMobile ? 24 : 40,
                }}>
                  {platformsCoverage.platforms.map((p) => (
                    <div key={p.name} title={p.name} style={{
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                    }}>
                      <img
                        src={p.icon}
                        alt={`${p.name} logo`}
                        width={36} height={36}
                        style={{ opacity: 0.85 }}
                      />
                      <span style={{
                        fontFamily: "var(--body)", fontSize: 12, color: TEXT_MUTED,
                        whiteSpace: "nowrap",
                      }}>{p.name}</span>
                    </div>
                  ))}
                </div>
              </FadeIn>
            </div>
          </section>
        )}

        {/* Trust Signals */}
        {trustSignals && (
          <section style={{ background: BG_ALT, padding: isMobile ? "80px 20px" : "120px 24px" }}>
            <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
              <FadeIn>
                <h2 style={{
                  fontFamily: "var(--display)", fontSize: "clamp(24px, 3vw, 32px)",
                  fontWeight: 600, letterSpacing: "-0.031em", color: TEXT, lineHeight: 1.2,
                  textAlign: "center", marginBottom: 48, maxWidth: 720, marginLeft: "auto", marginRight: "auto",
                }}>{trustSignals.heading}</h2>
              </FadeIn>
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: 20,
                maxWidth: 880, margin: "0 auto",
              }}>
                {trustSignals.items.map((item, i) => (
                  <FadeIn key={item.title} delay={i * 0.05}>
                    <div style={{
                      border: `1px solid ${CARD_BORDER}`, borderRadius: 12,
                      padding: 20,
                      display: "flex", gap: 14, alignItems: "flex-start",
                    }}>
                      {/* Checkmark */}
                      <div style={{
                        width: 22, height: 22, borderRadius: "50%",
                        background: "rgba(79,205,196,0.15)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, marginTop: 2,
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: "var(--display)", fontSize: 16, fontWeight: 600,
                          color: TEXT, marginBottom: item.subtext ? 6 : 0, lineHeight: 1.35,
                        }}>{item.title}</div>
                        {item.subtext && (
                          <div style={{
                            fontFamily: "var(--body)", fontSize: 14, color: TEXT_MUTED, lineHeight: 1.6,
                          }}>{item.subtext}</div>
                        )}
                      </div>
                    </div>
                  </FadeIn>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Connections */}
        {connections.length > 0 && (
          <section style={{ background: BG_ALT, padding: isMobile ? "80px 20px" : "120px 24px" }}>
            <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
              <FadeIn>
                <h2 style={{
                  fontFamily: "var(--display)", fontSize: "clamp(24px, 3vw, 32px)",
                  fontWeight: 600, letterSpacing: "-0.031em", color: TEXT, lineHeight: 1.2,
                  textAlign: "center", marginBottom: 40,
                }}>Works with your other AI-Advisors modules</h2>
              </FadeIn>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${Math.min(connections.length, 3)}, 1fr)`, gap: 16 }}>
                {connections.map((conn, i) => (
                  <FadeIn key={conn.title} delay={i * 0.08}>
                    <a href={conn.href} style={{
                      display: "block", border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: 24,
                      textDecoration: "none", transition: "border-color 0.2s",
                    }}
                      onMouseOver={(e) => e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"}
                      onMouseOut={(e) => e.currentTarget.style.borderColor = CARD_BORDER}
                    >
                      <div style={{ fontFamily: "var(--display)", fontSize: 17, fontWeight: 600, color: TEXT, marginBottom: 6 }}>{conn.title}</div>
                      <div style={{ fontFamily: "var(--body)", fontSize: 14, color: TEXT_MUTED, lineHeight: 1.6 }}>{conn.description}</div>
                    </a>
                  </FadeIn>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Related Content (uses shared RelatedPostCard from /blog/components) */}
        {resolvedRelatedPosts.length > 0 && (
          <section style={{ background: BG, padding: isMobile ? "80px 20px" : "120px 24px" }}>
            <div style={{ maxWidth: MAX_W, margin: "0 auto" }}>
              <FadeIn>
                <h2 style={{
                  fontFamily: "var(--display)", fontSize: "clamp(24px, 3vw, 32px)",
                  fontWeight: 600, letterSpacing: "-0.031em", color: TEXT, lineHeight: 1.2,
                  textAlign: "center", marginBottom: 40,
                }}>{relatedHeading}</h2>
              </FadeIn>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 20,
              }}>
                {resolvedRelatedPosts.map((post, i) => (
                  <FadeIn key={post.slug} delay={i * 0.08}>
                    <RelatedPostCard
                      slug={post.slug}
                      title={post.title}
                      category={post.category}
                      readTime={post.readTime}
                    />
                  </FadeIn>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQs */}
        {faqs.length > 0 && (
          <section style={{ background: BG, padding: isMobile ? "80px 20px" : "120px 24px" }}>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              // No fallback date. The old `|| "2026-03-25"` silently stamped any
              // page that forgot to pass lastUpdated with a date from March,
              // which is worse than omitting the property. Callers pass
              // pageDate(route) from app/lib/page-dates.js; if that is missing,
              // dateModified is dropped and audit-page-dates.mjs reports it.
              ...(toIsoDateTime(lastUpdated) ? { "dateModified": toIsoDateTime(lastUpdated) } : {}),
              "mainEntity": faqs.map((f) => ({
                "@type": "Question",
                "name": f.q,
                "acceptedAnswer": { "@type": "Answer", "text": f.a },
              })),
            }) }} />
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
              <FadeIn>
                <h2 style={{
                  fontFamily: "var(--display)", fontSize: "clamp(24px, 3vw, 32px)",
                  fontWeight: 600, letterSpacing: "-0.031em", color: TEXT,
                  marginBottom: 40,
                }}>Frequently Asked Questions</h2>
              </FadeIn>
              {faqs.map((faq, i) => (
                <FadeIn key={i} delay={i * 0.04}>
                  <div style={{ marginBottom: 32 }}>
                    <h3 style={{ fontFamily: "var(--display)", fontWeight: 600, fontSize: 17, color: TEXT, margin: "0 0 8px" }}>{faq.q}</h3>
                    <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT_MUTED, lineHeight: 1.7, margin: 0 }}>{faq.a}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </section>
        )}

        {/* CTA with animated grid background */}
        <section style={{ background: BG_ALT, padding: isMobile ? "80px 20px" : "140px 24px", position: "relative", overflow: "hidden" }}>
          {/* Animated grid */}
          <div style={{ position: "absolute", inset: 0, display: "grid", gridTemplateColumns: `repeat(${isMobile ? 6 : 12}, 1fr)`, gridTemplateRows: `repeat(${isMobile ? 4 : 6}, 1fr)`, gap: 1, pointerEvents: "none", zIndex: 0 }}>
            {Array.from({ length: isMobile ? 24 : 72 }).map((_, i) => (
              <div key={i} style={{
                background: "rgba(255,255,255,0.02)",
                animation: `gridPulse ${3 + (i % 5) * 0.8}s ease-in-out ${(i % 7) * 0.4}s infinite alternate`,
                borderRadius: 2,
              }} />
            ))}
          </div>
          {/* Gradient overlay to fade edges */}
          <div style={{ position: "absolute", inset: 0, background: isMobile ? `radial-gradient(ellipse 120% 80% at center, transparent 40%, ${BG_ALT} 90%)` : `radial-gradient(ellipse at center, transparent 30%, ${BG_ALT} 75%)`, pointerEvents: "none", zIndex: 1 }} />
          <FadeIn>
            <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 2 }}>
              <h2 style={{ ...h2Style, marginBottom: 16 }}>{cta.title || "Start tracking your AI visibility today"}</h2>
              <p style={{ fontFamily: "var(--body)", fontSize: 17, color: TEXT_MUTED, marginBottom: 32 }}>
                {cta.subtitle || "Start your 14-day free trial."}
              </p>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <PrimaryBtn white href={cta.primaryHref || "https://app.ai-advisors.ai/signup?utm_source=website&utm_medium=platform&utm_campaign=signup&utm_content=platform_cta"}>{cta.primaryLabel || "Start Free Trial"}</PrimaryBtn>
                {cta.secondaryLabel && cta.secondaryHref && (
                  <SecondaryBtn href={cta.secondaryHref}>{cta.secondaryLabel}</SecondaryBtn>
                )}
              </div>
            </div>
          </FadeIn>
        </section>
      </main>
      <Footer />
    </div>
  );
}
